import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ownerKey, useOwnerId } from "../features/auth/owner.ts";
import { api, query, type Schemas } from "./client.ts";

/**
 * Los favoritos del usuario: la estrella de un instrumento, de un broker o de
 * un setup (migracion 0014).
 *
 * DOS LISTAS Y NO UNA, y no es un descuido del contrato:
 *
 *   - `GET /favorites` trae TODAS las marcas, tambien las que apuntan a algo
 *     archivado. Es la que necesitan las pantallas de mantenimiento, que listan
 *     los archivados y tienen que pintar su estrella encendida en su fila.
 *   - `lookups.favorites` trae solo las OFRECIBLES, las que apuntan a algo
 *     activo. Es la que necesita el formulario de alta: un chip que ofrece un
 *     setup retirado ofrece elegir algo que el servidor ya no acepta.
 *
 * Por eso marcar invalida las dos: son la misma tabla vista con dos preguntas
 * distintas, y despues de mover una estrella las dos respuestas cambian.
 */

export type Favorite = Schemas["FavoriteDto"];
export type FavoriteEntityType = Schemas["FavoriteEntityType"];

/* El dueno detras del prefijo (ver `api/hooks.ts`): las estrellas de otro son
   otra cache, y marcar —que siempre es en la propia— invalida por prefijo. */
const keys = {
  favorites: (owner: string | undefined) => ["favorites", ownerKey(owner)] as const,
};

/**
 * La clave con la que se pregunta "esta marcado". El tipo va delante porque el
 * identificador solo no basta: los favoritos son polimorficos y un broker y un
 * setup podrian, en teoria, compartir identificador.
 */
export function favoriteKey(entityType: FavoriteEntityType, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/**
 * Las marcas como conjunto, que es como se preguntan: una fila de la tabla
 * quiere saber si ELLA esta marcada, no la lista entera. Con un array habria que
 * recorrerlo una vez por fila.
 */
export function useFavoriteSet(): { set: ReadonlySet<string>; pending: boolean } {
  const owner = useOwnerId();
  const favorites = useQuery({
    queryKey: keys.favorites(owner),
    queryFn: () => api.get<Favorite[]>(`/api/v1/favorites${query({ owner })}`),
    staleTime: 5 * 60_000,
  });

  return {
    set: new Set((favorites.data ?? []).map((item) => favoriteKey(item.entityType, item.entityId))),
    /* `pending` se expone para que quien ORDENE por favoritos pueda esperarlos:
       pintar la lista sin ellos y reordenarla cuando lleguen hace que salte bajo
       el dedo. Deja de ser cierto en cuanto la peticion falla, y eso es lo que
       hace falta: un fallo de los favoritos no puede dejar la tabla colgada. */
    pending: favorites.isPending,
  };
}

export type ToggleFavorite = {
  entityType: FavoriteEntityType;
  entityId: string;
  /** Lo que se quiere DESPUES del gesto, no lo que hay ahora. */
  on: boolean;
};

/**
 * Marcar y desmarcar, en una sola mutacion, porque es un solo gesto: la estrella
 * se pulsa y cambia. Las dos rutas son idempotentes (marcar dos veces es 204),
 * asi que un doble toque no rompe nada.
 *
 * La lista se actualiza ANTES de que la peticion vuelva. Una estrella que tarda
 * en encenderse se pulsa otra vez, y entonces la segunda pulsacion la apaga: el
 * usuario acaba con lo contrario de lo que pidio. Si el servidor dice no, la
 * lista vuelve a como estaba.
 */
export function useToggleFavorite() {
  const client = useQueryClient();
  /* Se marca SIEMPRE en la propia: el servidor no acepta dueno al escribir. La
     estrella solo se ofrece mirando la propia, asi que esta es la lista que
     esta a la vista. */
  const own = keys.favorites(undefined);

  return useMutation({
    mutationFn: (input: ToggleFavorite) => {
      const path = `/api/v1/favorites/${input.entityType}/${input.entityId}`;

      return input.on ? api.put<void>(path) : api.del<void>(path);
    },
    onMutate: async (input: ToggleFavorite) => {
      /* Sin cancelar, una lectura en vuelo aterrizaria despues y devolveria la
         estrella a su estado viejo. */
      await client.cancelQueries({ queryKey: own });

      const previous = client.getQueryData<Favorite[]>(own);

      client.setQueryData<Favorite[]>(own, (current) => {
        const rest = (current ?? []).filter(
          (item) => !(item.entityType === input.entityType && item.entityId === input.entityId),
        );

        return input.on
          ? [...rest, { entityType: input.entityType, entityId: input.entityId }]
          : rest;
      });

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous !== undefined) {
        client.setQueryData(own, context.previous);
      }
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ["favorites"] });
      void client.invalidateQueries({ queryKey: ["lookups"] });
    },
  });
}
